import React, { useState, useEffect, useCallback } from "react";
import { authFetch, getUser } from "../pages/Auth";
import { COLORS } from "../chartTheme";

const EHR_SYSTEMS = [
  { id: "athena", name: "Athena Health", icon: "🏥", fields: ["client_id", "client_secret", "base_url", "practice_id"] },
  { id: "epic", name: "Epic", icon: "🏛", fields: ["client_id", "private_key", "base_url", "token_url"] },
  { id: "cerner", name: "Cerner / Oracle Health", icon: "☁️", fields: ["client_id", "client_secret", "base_url", "token_url"] },
  { id: "canvas", name: "Canvas Medical", icon: "🎨", fields: ["client_id", "client_secret", "base_url"] },
  { id: "csv", name: "CSV Upload", icon: "📄", fields: [] },
  { id: "gcs_fhir", name: "GCS · FHIR Bundle", icon: "📦", fields: ["bucket", "file_path"], kind: "gcs" },
  { id: "gcs_ndjson", name: "GCS · Bulk FHIR (NDJSON)", icon: "📚", fields: ["bucket", "file_path"], kind: "gcs" },
  { id: "gcs_hl7", name: "GCS · HL7 v2 (.hl7/.txt)", icon: "📨", fields: ["bucket", "file_path"], kind: "gcs" },
];

// ═══════════════════════════════════════════════════════════
// CONNECTION WIZARD
// ═══════════════════════════════════════════════════════════

function ConnectionWizard({ onClose, onSync }) {
  const [step, setStep] = useState(1);
  const [source, setSource] = useState(null);
  const [config, setConfig] = useState({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const setField = (k, v) => setConfig({ ...config, [k]: v });

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    // Simple validation — real test would hit the EHR
    await new Promise(r => setTimeout(r, 1000));
    const missing = (source?.fields || []).filter(f => !config[f]);
    if (missing.length > 0) {
      setTestResult({ ok: false, msg: `Missing fields: ${missing.join(", ")}` });
    } else {
      setTestResult({ ok: true, msg: "Connection settings validated. Ready to sync." });
    }
    setTesting(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const isGcs = source.kind === "gcs";
      const endpoint = isGcs ? "/api/gcs/process" : "/api/admin/sync";
      const payload = isGcs
        ? { bucket: config.bucket, file_path: config.file_path }
        : { source_system: source.id, config, full_sync: true };
      const res = await authFetch(endpoint, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setSyncResult({ ok: true, data });
        if (onSync) onSync(data);
      } else {
        setSyncResult({ ok: false, msg: data.error || data.detail || "Sync failed" });
      }
    } catch {
      setSyncResult({ ok: false, msg: "Unable to connect to server" });
    }
    setSyncing(false);
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.wizard} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, paddingBottom: 14, borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Connect Data Source</div>
          <button onClick={onClose} style={s.closeBtn}>&times;</button>
        </div>

        {/* Step 1: Select source */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 16 }}>Select your EHR system</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {EHR_SYSTEMS.map(ehr => (
                <button key={ehr.id} onClick={() => { setSource(ehr); setStep(2); }}
                  style={{
                    ...s.ehrCard,
                    borderColor: source?.id === ehr.id ? COLORS.primary : COLORS.border,
                  }}>
                  <span style={{ fontSize: 24, marginBottom: 8, display: "block" }}>{ehr.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{ehr.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Configure */}
        {step === 2 && source && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ fontSize: 20 }}>{source.icon}</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{source.name}</span>
            </div>

            {source.id === "csv" ? (
              <div style={{ color: COLORS.muted, fontSize: 12, lineHeight: 1.6, padding: 16, background: COLORS.bg, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                CSV import uses the CLI:<br /><br />
                <code style={{ color: COLORS.primary, fontSize: 11 }}>
                  python3 ingestion_engine.py --source csv --customer-id YOUR_ID --patients-file patients.csv --observations-file observations.csv
                </code>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {source.fields.map(field => (
                  <div key={field}>
                    <label style={s.label}>{field.replace(/_/g, " ")}</label>
                    {field === "private_key" ? (
                      <textarea value={config[field] || ""} onChange={e => setField(field, e.target.value)}
                        placeholder={`Paste ${field}`} rows={4} style={{ ...s.input, resize: "vertical" }} />
                    ) : (
                      <input type={field.includes("secret") || field.includes("key") ? "password" : "text"}
                        value={config[field] || ""} onChange={e => setField(field, e.target.value)}
                        placeholder={PLACEHOLDERS[field] || field} style={s.input} />
                    )}
                  </div>
                ))}
              </div>
            )}

            {testResult && (
              <div style={{
                marginTop: 12, padding: 10, borderRadius: 6, fontSize: 12,
                background: testResult.ok ? "rgba(52,211,153,0.08)" : "rgba(255,77,109,0.08)",
                color: testResult.ok ? "#34d399" : "#ff4d6d",
                border: `1px solid ${testResult.ok ? "rgba(52,211,153,0.2)" : "rgba(255,77,109,0.2)"}`,
              }}>{testResult.msg}</div>
            )}

            {syncResult && (
              <div style={{
                marginTop: 12, padding: 12, borderRadius: 6, fontSize: 12,
                background: syncResult.ok ? "rgba(52,211,153,0.08)" : "rgba(255,77,109,0.08)",
                color: syncResult.ok ? "#34d399" : "#ff4d6d",
                border: `1px solid ${syncResult.ok ? "rgba(52,211,153,0.2)" : "rgba(255,77,109,0.2)"}`,
              }}>
                {syncResult.ok ? (
                  source.kind === "gcs" ? (
                    <div>
                      Processed {syncResult.data.file_path || "file"}: {syncResult.data.patients_created || 0} patients,
                      {" "}{syncResult.data.observations_created || 0} observations
                      {syncResult.data.format ? ` (${syncResult.data.format})` : ""}
                    </div>
                  ) : (
                    <div>
                      Sync complete: {syncResult.data.patients_created} patients created,
                      {syncResult.data.patients_updated} updated,
                      {syncResult.data.observations_created} observations
                      ({syncResult.data.duration_seconds}s)
                    </div>
                  )
                ) : syncResult.msg}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={() => setStep(1)} style={s.backBtn}>Back</button>
              {source.id !== "csv" && (
                <>
                  <button onClick={handleTest} disabled={testing} style={s.testBtn}>
                    {testing ? "Testing..." : "Test Connection"}
                  </button>
                  <button onClick={handleSync} disabled={syncing || !testResult?.ok} style={{
                    ...s.syncBtn, opacity: testResult?.ok ? 1 : 0.4,
                  }}>
                    {syncing ? "Syncing..." : "Start Sync"}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const PLACEHOLDERS = {
  client_id: "OAuth Client ID",
  client_secret: "OAuth Client Secret",
  base_url: "https://api.example.com/fhir/r4",
  token_url: "https://api.example.com/oauth2/token",
  practice_id: "Practice / Tenant ID",
  private_key: "-----BEGIN RSA PRIVATE KEY-----\n...",
  bucket: "pathriva-ingest-prod",
  file_path: "customer_id/patients_2026_04.json",
};

// ═══════════════════════════════════════════════════════════
// SYNC HISTORY
// ═══════════════════════════════════════════════════════════

function SyncHistory() {
  const [syncs, setSyncs] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/admin/sync-history");
      const data = await res.json();
      if (res.ok) setSyncs(data.syncs);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={s.card}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Sync History</div>
      {loading ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>Loading...</div>
      ) : !syncs || syncs.length === 0 ? (
        <div style={{ color: COLORS.muted, fontSize: 12, padding: 20, textAlign: "center" }}>No syncs yet. Connect an EHR to get started.</div>
      ) : (
        <div style={{ maxHeight: 250, overflowY: "auto" }}>
          {syncs.map((s, i) => (
            <div key={s.event_id || i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 0", borderBottom: `1px solid ${COLORS.border}08`,
            }}>
              <div>
                <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{s.source_system}</span>
                <span style={{ fontSize: 10, color: COLORS.muted, marginLeft: 8 }}>{s.customer_id}</span>
              </div>
              <span style={{ fontSize: 10, color: COLORS.muted, fontFamily: "monospace" }}>
                {s.timestamp ? new Date(s.timestamp).toLocaleString() : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PANEL
// ═══════════════════════════════════════════════════════════

export default function IngestionPanel() {
  const [showWizard, setShowWizard] = useState(false);
  const user = getUser();

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Data Ingestion</div>
        {user?.role === "super_admin" && (
          <button onClick={() => setShowWizard(true)} style={s.connectBtn}>+ Connect Source</button>
        )}
      </div>

      {/* Connected Systems Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
        {[
          { label: "Connectors Available", value: "8", sub: "EHRs · CSV · GCS (FHIR/NDJSON/HL7)" },
          { label: "FHIR Version", value: "R4 + R5", sub: "Dual version support" },
          { label: "GLP-1 Medications", value: "25", sub: "RxNorm codes mapped" },
        ].map(m => (
          <div key={m.label} style={s.card}>
            <div style={{ fontSize: 9, color: COLORS.muted, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>{m.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary }}>{m.value}</div>
            <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 2 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      <SyncHistory />

      {showWizard && <ConnectionWizard onClose={() => setShowWizard(false)} onSync={() => setShowWizard(false)} />}
    </div>
  );
}

const s = {
  card: { background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 16 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center", padding: 20 },
  wizard: { width: "100%", maxWidth: 500, maxHeight: "85vh", background: "#0d1117", border: `1px solid ${COLORS.border}`, borderRadius: 12, overflowY: "auto", padding: 24 },
  closeBtn: { background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.muted, padding: "8px 12px", fontSize: 13, cursor: "pointer" },
  ehrCard: { background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 20, cursor: "pointer", textAlign: "center", fontFamily: "inherit", transition: "border-color 0.15s" },
  label: { fontSize: 10, color: COLORS.muted, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 4 },
  input: { width: "100%", background: "#131920", border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "8px 12px", color: COLORS.text, fontSize: 12, fontFamily: "inherit", outline: "none" },
  backBtn: { background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, color: COLORS.muted, padding: "8px 16px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" },
  testBtn: { background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.3)", borderRadius: 6, color: "#60a5fa", padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  syncBtn: { background: COLORS.primary, border: "none", borderRadius: 6, color: "#000", padding: "8px 20px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  connectBtn: { background: "linear-gradient(135deg, #00e5ff, #0099cc)", border: "none", borderRadius: 6, color: "#000", padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
};
